import { useTranslation } from "react-i18next";

import { DownloadIcon, ImagesIcon, Trash2Icon } from "@lucide/icons";

import { FileChooserDropzoneTrigger } from "simplydo/react/components/Choosers/FileChooser/FileChooserDropzoneTrigger";
import { allowedImageTypesList } from "simplydo/react/components/Choosers/FileChooser/uploadUtils";
import { styled } from "simplydo/react/styled";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import {
  Card,
  CardContent,
  CardGroup,
  CardHeader,
} from "simplydo/react/ui/card";
import { Icon } from "simplydo/react/ui/icon";
import { Segment } from "simplydo/react/ui/segment";

import { ImageChooser } from "components/lib/Choosers";

import { FieldProps } from "./FieldProps";

const StyledImage = styled.img`
  max-width: 100%;
  display: block;
  object-fit: contain;
`;

type ImageComponentProps = { image: string; onRemove?: () => void };

const ImageComponent = ({ image, onRemove }: ImageComponentProps) => {
  const { t } = useTranslation();

  return (
    <CardGroup>
      <Card style={{ width: "80%" }}>
        <CardContent style={{ color: "#808080" }}>
          <Icon icon={ImagesIcon} />
          Uploaded image
        </CardContent>
        <CardContent textAlign="center">
          <CardHeader style={{ overflowWrap: "break-word" }}>
            <StyledImage
              alt={`${t("common:capitalise", { key: "generic.idea" })} field content`}
              src={image}
            />
          </CardHeader>
        </CardContent>
        <CardContent extra>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 10,
            }}
          >
            <ButtonLink
              fluid
              style={{ marginLeft: 0, marginRight: 0 }}
              size="small"
              variant="outline"
              to={image}
              target="_blank"
              rel="noopener noreferrer"
              download={image}
            >
              <Icon icon={DownloadIcon} />
              {t("generic.download")}
            </ButtonLink>
            {onRemove ? (
              <Button
                size="small"
                onClick={onRemove}
                style={{ marginLeft: 0, marginRight: 0 }}
                variant="outline"
              >
                <Icon icon={Trash2Icon} />
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </CardGroup>
  );
};

const Image = ({
  field,
  ideaId,
  fieldValue = "",
  updateIdea,
  isEditing,
}: FieldProps) => {
  const imageSrc = typeof fieldValue === "string" ? fieldValue : "";
  if (!isEditing) {
    if (imageSrc) {
      return <ImageComponent image={imageSrc} />;
    }
    return null;
  }

  if (imageSrc) {
    return (
      <Segment placeholder>
        <ImageComponent
          image={imageSrc}
          onRemove={() => updateIdea(null, ["templated", field.id])}
        />
      </Segment>
    );
  }

  return (
    <ImageChooser
      trigger={
        <FileChooserDropzoneTrigger
          forType="idea"
          accept={allowedImageTypesList.join(",")}
          forId={ideaId}
          onComplete={(_value, _name, imageSrc) =>
            updateIdea(imageSrc, ["templated", field.id])
          }
          dropzoneProps={{
            title: "Upload an image",
            DropZoneComponent: Segment,
            defaultIcon: ImagesIcon,
          }}
        />
      }
      forType="idea"
      forId={ideaId}
      onComplete={(_value, _name, imageSrc) =>
        updateIdea(imageSrc, ["templated", field.id])
      }
    />
  );
};

export default Image;
